import { zodResolver } from '@hookform/resolvers/zod'
import { NavLink, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { FormCard } from '../../components/ui/FormCard'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'
import { broadcastSchema } from '../../schemas/adminSchemas'

export function BroadcastFormPage() {
  const navigate = useNavigate()
  const form = useForm<z.infer<typeof broadcastSchema>>({
    resolver: zodResolver(broadcastSchema),
    mode: 'onBlur',
    defaultValues: {
      title: '',
      body: '',
      imageCount: undefined,
      deepLink: '',
      audience: 'All users',
      scheduleMode: 'Send now',
      sendAt: '',
    },
  })

  const scheduleMode = form.watch('scheduleMode')

  async function submit(values: z.infer<typeof broadcastSchema>) {
    try {
      await api.post('/admin/notifications/broadcasts', {
        title: values.title,
        body: values.body,
        audience: values.audience,
        scheduleMode: values.scheduleMode,
        sendAt: values.scheduleMode === 'Schedule for' ? values.sendAt : undefined,
        deepLink: values.deepLink || undefined,
        imageCount: values.imageCount ?? 0,
      })
      navigate('/notifications/broadcasts')
    } catch {
      form.setError('root', { message: 'Unable to save broadcast. Please try again.' })
    }
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Broadcast Composer"
        subtitle="Compose customer push notification, choose audience, image, deep link, and schedule in IST."
        action={<NavLink to="/notifications/broadcasts"><Button variant="ghost">Back</Button></NavLink>}
      />
      {form.formState.errors.root?.message ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {form.formState.errors.root.message}
        </div>
      ) : null}
      <FormCard
        title="Broadcast Form"
        submitLabel={scheduleMode === 'Send now' ? 'Send Broadcast' : 'Schedule Broadcast'}
        onSubmit={form.handleSubmit(submit, () => {
          form.setError('root', { message: 'Please fix the highlighted fields.' })
        })}
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title" error={form.formState.errors.title?.message} {...form.register('title')} />
          <Field as="select" label="Audience" error={form.formState.errors.audience?.message} {...form.register('audience')}>
            <option>All users</option><option>Active 30 days</option><option>Has placed an order</option><option>By city</option>
          </Field>
          <div className="lg:col-span-2">
            <Field as="textarea" label="Body" error={form.formState.errors.body?.message} {...form.register('body')} />
          </div>
          <Field label="Image Count (optional)" type="number" min={0} max={1} error={form.formState.errors.imageCount?.message} {...form.register('imageCount', { valueAsNumber: true })} />
          <Field label="Deep Link (optional)" error={form.formState.errors.deepLink?.message} placeholder="seasonly://product/123" {...form.register('deepLink')} />
          <Field as="select" label="Schedule" error={form.formState.errors.scheduleMode?.message} {...form.register('scheduleMode')}>
            <option>Send now</option><option>Schedule for</option>
          </Field>
          {scheduleMode === 'Schedule for' && (
            <Field label="Send At" type="datetime-local" error={form.formState.errors.sendAt?.message} {...form.register('sendAt')} />
          )}
        </div>
      </FormCard>
    </div>
  )
}
